import { formatApiErrorDetail } from "@/lib/api";
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FolderOpen, UploadSimple, DownloadSimple, Trash, FilePdf, File as FileIcon,
  ImageSquare, X,
} from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

const KIND_OPTIONS = [
  { value: "lease",         label: "Lease" },
  { value: "insurance",     label: "Insurance" },
  { value: "permit",        label: "Permit" },
  { value: "w9",            label: "W-9" },
  { value: "warranty",      label: "Warranty" },
  { value: "inspection",    label: "Inspection Report" },
  { value: "utility_bill",  label: "Utility Bill" },
  { value: "invoice",       label: "Invoice" },
  { value: "photo",         label: "Photo" },
  { value: "other",         label: "Other" },
];

const KIND_STYLES = {
  lease:        "bg-purple-100 text-purple-800 border-purple-500",
  insurance:    "bg-blue-100 text-blue-800 border-blue-500",
  permit:       "bg-amber-100 text-amber-800 border-amber-500",
  w9:           "bg-emerald-100 text-emerald-800 border-emerald-500",
  warranty:     "bg-cyan-100 text-cyan-800 border-cyan-500",
  inspection:   "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-500",
  utility_bill: "bg-yellow-100 text-yellow-800 border-yellow-500",
  invoice:      "bg-green-100 text-green-800 border-green-500",
  photo:        "bg-pink-100 text-pink-800 border-pink-500",
  other:        "bg-slate-100 text-slate-700 border-slate-400",
};

function humanBytes(b) {
  if (!b) return "0 KB";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function iconFor(ct = "") {
  if (ct.startsWith("image/")) return ImageSquare;
  if (ct.includes("pdf")) return FilePdf;
  return FileIcon;
}

/**
 * PropertyFilesPanel — per-property document vault (AppFolio parity).
 * Renders inside UnitDetail / PropertySnapshot. Scoped by street address —
 * every unit at the same building shares the same files drawer.
 */
export function PropertyFilesPanel({ address }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [kindFilter, setKindFilter] = useState("");
  const [draft, setDraft] = useState({ kind: "lease", notes: "", file: null });
  const inputRef = useRef(null);

  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const load = () => {
    if (!address) return;
    setLoading(true);
    axios.get(`${BASE}/api/property-files`, {
      ...auth(),
      params: { address, ...(kindFilter ? { kind: kindFilter } : {}) },
    })
      .then(r => setFiles(r.data.files || []))
      .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed to load files")))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [address, kindFilter]);

  const doUpload = async () => {
    if (!draft.file) return toast.error("Pick a file first");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("address", address);
      fd.append("kind", draft.kind || "other");
      fd.append("notes", draft.notes || "");
      fd.append("file", draft.file);
      await axios.post(`${BASE}/api/property-files/upload`, fd, {
        headers: { ...auth().headers, "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${draft.file.name}`);
      setDraft({ kind: "lease", notes: "", file: null });
      if (inputRef.current) inputRef.current.value = "";
      setUploadOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Upload failed"));
    } finally { setUploading(false); }
  };

  const doDelete = async (f) => {
    if (!window.confirm(`Delete ${f.filename}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${BASE}/api/property-files/${f.id}`, auth());
      toast.success("Deleted");
      setFiles(prev => prev.filter(x => x.id !== f.id));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Delete failed"));
    }
  };

  const downloadUrl = (f) =>
    `${BASE}/api/property-files/${f.id}/download`;

  return (
    <div className="mt-4 rugged-card p-4 bg-white border-l-4 border-purple-500" data-testid="property-files-panel">
      <div className="flex items-center gap-2">
        <FolderOpen weight="fill" size={20} className="text-purple-600"/>
        <h3 className="font-display font-black text-sm uppercase tracking-wider">Property Files</h3>
        <span className="text-xs text-slate-500 font-bold">· {files.length}</span>
        <div className="ml-auto flex items-center gap-2">
          <select value={kindFilter} onChange={e => setKindFilter(e.target.value)}
            data-testid="property-files-kind-filter"
            className="h-8 px-2 border-2 border-slate-300 text-[11px] font-bold">
            <option value="">All types</option>
            {KIND_OPTIONS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
          <button onClick={() => setUploadOpen(true)} data-testid="property-files-upload-btn"
            className="h-8 px-3 bg-purple-600 hover:bg-purple-700 text-white font-black uppercase text-[9px] tracking-widest inline-flex items-center gap-1">
            <UploadSimple size={12}/> Upload
          </button>
        </div>
      </div>

      {loading && <div className="mt-3 text-xs text-slate-500 font-bold">Loading files…</div>}
      {!loading && files.length === 0 && (
        <div className="mt-3 p-4 border-2 border-dashed border-slate-300 bg-slate-50 text-center" data-testid="property-files-empty">
          <FolderOpen size={36} className="mx-auto text-slate-400 mb-1"/>
          <div className="text-xs font-bold text-slate-500">No files yet for this property.</div>
          <div className="text-[10px] text-slate-400 mt-1">
            Upload leases, insurance certs, permits, W-9s, warranty docs, inspection PDFs…
          </div>
          <button onClick={() => setUploadOpen(true)}
            data-testid="property-files-empty-upload"
            className="mt-3 h-8 px-3 bg-purple-600 hover:bg-purple-700 text-white font-black uppercase text-[9px] tracking-widest inline-flex items-center gap-1">
            <UploadSimple size={12}/> Upload first file
          </button>
        </div>
      )}
      {!loading && files.length > 0 && (
        <div className="mt-3 divide-y divide-slate-200 border-2 border-slate-200">
          {files.map(f => {
            const Icon = iconFor(f.content_type);
            const kindStyle = KIND_STYLES[f.kind] || KIND_STYLES.other;
            return (
              <div key={f.id} className="flex items-center gap-2 p-2 hover:bg-slate-50" data-testid={`property-file-${f.id}`}>
                <Icon size={22} className="text-slate-500 flex-shrink-0"/>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-bold text-slate-800 truncate" title={f.filename}>{f.filename}</div>
                  <div className="text-[10px] text-slate-500 flex items-center gap-2 flex-wrap">
                    <span className={`px-1.5 py-0.5 border font-black uppercase tracking-widest text-[8px] ${kindStyle}`}>
                      {f.kind}
                    </span>
                    <span>{humanBytes(f.size_bytes)}</span>
                    <span>· {f.uploaded_by || "—"}</span>
                    <span>· {f.uploaded_at ? new Date(f.uploaded_at).toLocaleDateString() : ""}</span>
                    {f.notes && <span className="text-slate-400">· {f.notes}</span>}
                  </div>
                </div>
                <a href={downloadUrl(f)} target="_blank" rel="noreferrer"
                  data-testid={`property-file-download-${f.id}`}
                  className="h-8 w-8 flex items-center justify-center border-2 border-slate-300 hover:border-[#0f172a] hover:bg-slate-100"
                  title="Download">
                  <DownloadSimple size={14}/>
                </a>
                <button onClick={() => doDelete(f)}
                  data-testid={`property-file-delete-${f.id}`}
                  className="h-8 w-8 flex items-center justify-center text-red-600 hover:bg-red-50 border-2 border-transparent hover:border-red-500"
                  title="Delete">
                  <Trash size={14}/>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {uploadOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="property-files-upload-modal">
          <div className="max-w-md w-full bg-white border-2 border-[#0f172a] shadow-2xl">
            <div className="px-4 py-3 border-b-2 border-[#0f172a] flex items-center justify-between bg-purple-50">
              <div className="font-display font-black text-lg uppercase tracking-tight">Upload File</div>
              <button onClick={() => setUploadOpen(false)} className="h-8 w-8 flex items-center justify-center hover:bg-slate-200">
                <X size={18}/>
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">
                  Document Type
                </label>
                <select value={draft.kind} onChange={e => setDraft({...draft, kind: e.target.value})}
                  data-testid="upload-kind-select"
                  className="w-full h-10 px-2 border-2 border-slate-300 text-sm">
                  {KIND_OPTIONS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">
                  File
                </label>
                <input ref={inputRef} type="file"
                  onChange={e => setDraft({...draft, file: e.target.files?.[0] || null})}
                  data-testid="upload-file-input"
                  className="w-full text-sm"/>
                {draft.file && (
                  <div className="mt-1 text-[11px] text-slate-500">
                    {draft.file.name} · {humanBytes(draft.file.size)}
                  </div>
                )}
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 block">
                  Notes (optional)
                </label>
                <input value={draft.notes} onChange={e => setDraft({...draft, notes: e.target.value})}
                  placeholder="e.g. Renewed 2026-02, expires 2027"
                  data-testid="upload-notes-input"
                  className="w-full h-10 px-2 border-2 border-slate-300 text-sm"/>
              </div>
              <div className="text-[10px] text-slate-500">
                Max 25 MB per file. Scoped to <b>{address}</b>.
              </div>
            </div>
            <div className="p-3 border-t-2 border-[#0f172a] flex gap-2 justify-end bg-slate-50">
              <button onClick={() => setUploadOpen(false)}
                className="h-10 px-3 border-2 border-slate-400 font-black uppercase text-[11px] tracking-widest">
                Cancel
              </button>
              <button onClick={doUpload} disabled={uploading || !draft.file}
                data-testid="upload-submit-btn"
                className="h-10 px-4 bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1">
                <UploadSimple size={14}/> {uploading ? "Uploading…" : "Upload"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
